import React from 'react';
import {
  Dialog as DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from './primitives/dialog';
import cn from './primitives/cn';
import CloseButton from './CloseButton';

/**
 * `Modal.Header closeButton` has to be able to close the dialog, and no call site
 * passes it a handler: react-bootstrap supplied one through context and the screens
 * were written against that. This is the same arrangement.
 */
const ModalContext = React.createContext({ onHide: undefined });

/**
 * react-bootstrap's `Modal`, reimplemented on Radix behind the same API.
 *
 * It is no longer a passthrough. The eleven screens that use `Modal.Header` /
 * `Modal.Body` / `Modal.Footer` were the last thing holding react-bootstrap in the app,
 * and rewriting them all to `Dialog` would have meant eleven separate visible changes to
 * working screens. Matching the old prop contract instead means none of them changed at
 * all: `show`, `onHide`, `size`, `backdrop`, `fullscreen` and the class-name props all
 * still do what they did, and `animation` is accepted and ignored exactly as before.
 *
 * **Prefer `Dialog` for anything new.** It is the CMS shape, with props named for
 * behaviour. This exists to carry the screens that predate it.
 *
 * What the screens gain for free: focus is trapped and restored, the page behind is
 * marked `aria-hidden`, and `Modal.Title` is wired to `aria-labelledby`. What they do not
 * gain is a redesign; the padding, rules and type are the same as `Dialog`'s.
 */
const SIZE_CLASS = {
  sm: 'tw-max-w-dialog-sm',
  lg: 'tw-max-w-dialog-lg',
  xl: 'tw-max-w-dialog-xl',
};

/**
 * react-bootstrap's `fullscreen`: `true` fills the window at every width, and a breakpoint
 * ("sm-down" and so on) fills it only below that width. Every value used to mean `true`, so
 * the Audience Selector ("sm-down") filled a desktop window, its footer a screen below the
 * few rows it had (SURF-4875).
 */
export const FULLSCREEN_CLASS = {
  true: 'tw-max-w-none tw-w-dialog-full tw-h-dialog-full',
  'sm-down': 'max-sm:tw-max-w-none max-sm:tw-w-dialog-full max-sm:tw-h-dialog-full',
  'md-down': 'max-md:tw-max-w-none max-md:tw-w-dialog-full max-md:tw-h-dialog-full',
  'lg-down': 'max-lg:tw-max-w-none max-lg:tw-w-dialog-full max-lg:tw-h-dialog-full',
  'xl-down': 'max-xl:tw-max-w-none max-xl:tw-w-dialog-full max-xl:tw-h-dialog-full',
  'xxl-down': 'max-2xl:tw-max-w-none max-2xl:tw-w-dialog-full max-2xl:tw-h-dialog-full',
};

const Modal = ({
  show = false,
  onHide,
  // Accepted and ignored: react-bootstrap's fade. Radix has its own, in tailwind.config.
  animation, // eslint-disable-line no-unused-vars
  backdrop = true,
  size,
  fullscreen,
  className,
  dialogClassName,
  contentClassName,
  backdropClassName, // eslint-disable-line no-unused-vars
  children,
  ...rest
}) => {
  const handleOpenChange = (open) => {
    if (!open && onHide) onHide();
  };
  const block = (event) => event.preventDefault();
  // `backdrop="static"` means a click outside does not dismiss. Escape still does, which
  // is what react-bootstrap did too.
  const isStatic = backdrop === 'static';

  const context = React.useMemo(() => ({ onHide }), [onHide]);

  return (
    <DialogRoot open={!!show} onOpenChange={handleOpenChange}>
      <DialogContent
        showClose={false}
        className={cn(
          'modal-content',
          SIZE_CLASS[size],
          fullscreen && FULLSCREEN_CLASS[fullscreen === true ? 'true' : fullscreen],
          'tw-p-0',
          className,
          dialogClassName,
          contentClassName
        )}
        onPointerDownOutside={isStatic ? block : undefined}
        onInteractOutside={isStatic ? block : undefined}
        {...rest}
      >
        <ModalContext.Provider value={context}>{children}</ModalContext.Provider>
      </DialogContent>
    </DialogRoot>
  );
};

/**
 * `closeButton` is react-bootstrap's prop for the × in the corner. It renders the same
 * glyph the rest of the CMS uses rather than Radix's, so a converted header does not
 * suddenly carry a different icon set.
 */
Modal.Header = ({ closeButton = false, className, children, onHide, ...rest }) => {
  const fromContext = React.useContext(ModalContext).onHide;
  const close = onHide || fromContext;
  return (
    <DialogHeader className={cn('modal-header', className)} {...rest}>
      <div className="tw-flex-1 tw-min-w-0">{children}</div>
      {closeButton && <CloseButton onClick={close} />}
    </DialogHeader>
  );
};
Modal.Header.displayName = 'Modal.Header';

Modal.Title = ({ className, ...rest }) => (
  <DialogTitle className={cn('modal-title', className)} {...rest} />
);
Modal.Title.displayName = 'Modal.Title';

Modal.Body = ({ className, ...rest }) => (
  <DialogBody className={cn('modal-body', className)} {...rest} />
);
Modal.Body.displayName = 'Modal.Body';

Modal.Footer = ({ className, ...rest }) => (
  <DialogFooter className={cn('modal-footer', className)} {...rest} />
);
Modal.Footer.displayName = 'Modal.Footer';

export default Modal;
